import "../index.ts";
import { DEFAULT_EXCLUDE, DEFAULT_PALETTE, splitEmoji, type RoomEmoji } from "@live-reaccs/core";
import type { DisplayOptions, LiveReactionsElement } from "../display.ts";
import { $, agentPrompt, describeRoomEmoji, qrSvg } from "./shared.ts";

interface CreatedRoom {
  slug: string;
  title: string;
  emoji: RoomEmoji;
  tier: number;
  audienceUrl: string;
  presenterUrl: string;
  statusUrl: string;
  displayUrl: string;
  qrUrl: string;
  scriptUrl: string;
  createdAt: string;
}

const TOKEN_KEY = "live-reaccs:admin-token";
const ROOMS_KEY = "live-reaccs:rooms";

const form = $<HTMLFormElement>("#create");
const token = $<HTMLInputElement>("#token");
const tokenField = $("#token-field");
const palette = $<HTMLInputElement>("#palette");
const exclude = $<HTMLInputElement>("#exclude");
const animation = $<HTMLSelectElement>("#animation");
const speed = $<HTMLInputElement>("#speed");
const error = $("#error");
const overlay = $<LiveReactionsElement>("live-reactions");
let current: CreatedRoom | undefined;

token.value = localStorage.getItem(TOKEN_KEY) ?? "";
tokenField.hidden = !token.value;
palette.value = DEFAULT_PALETTE;
exclude.value = DEFAULT_EXCLUDE;

const mode = () => new FormData(form).get("mode") as RoomEmoji["mode"];
/** left / right / everywhere; lives in the links, not the room key, so it can change after creation */
const position = () => new FormData(form).get("position") as "bottom-left" | "bottom-right" | "bottom";
/** The page's own overlay (ambient demo, then the room's live reactions) follows the chosen settings. */
function preview(): void {
  $("#speed-value").textContent = `${Number(speed.value).toFixed(1)}×`;
  overlay.configure({
    origin: position(),
    animation: animation.value as DisplayOptions["animation"],
    speed: Number(speed.value),
  });
}

// the slider updates live while dragging (change only fires on release)
speed.addEventListener("input", () => {
  preview();
  if (current) show(current);
});

form.addEventListener("change", () => {
  $("#palette-field").hidden = mode() !== "palette";
  $("#exclude-field").hidden = mode() !== "open";
  preview();
  if (current) show(current);
});

// Turnstile (bot check for anonymous minting) only when the server has a site key
declare const turnstile: { render(el: HTMLElement, opts: { sitekey: string; theme: string }): string; reset(id: string): void } | undefined;
const siteKey = document.querySelector<HTMLMetaElement>('meta[name="turnstile-site-key"]')?.content ?? "";
let widget: string | undefined;
if (siteKey) {
  const script = document.createElement("script");
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
  script.async = true;
  script.onload = () => (widget = turnstile?.render($("#turnstile"), { sitekey: siteKey, theme: "dark" }));
  document.head.append(script);
}

form.addEventListener("submit", async (e) => {
  e.preventDefault();
  error.textContent = "";
  const data = new FormData(form);
  const turnstileToken = data.get("cf-turnstile-response");
  const res = await fetch("/api/rooms", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...(token.value && { authorization: `Bearer ${token.value}` }),
    },
    body: JSON.stringify({
      title: data.get("title"),
      emoji: mode() === "palette" ? { mode: "palette", emoji: palette.value } : { mode: "open", exclude: exclude.value },
      ...(turnstileToken && { turnstile: turnstileToken }),
    }),
  });
  if (widget) turnstile?.reset(widget); // tokens are single-use
  if (res.status === 401) {
    // server isn't open: ask for the admin token
    error.textContent = token.value ? "Admin token rejected." : "This server requires an admin token.";
    tokenField.hidden = false;
    token.focus();
    return;
  }
  if (!res.ok) {
    error.textContent = await res.text();
    return;
  }
  if (token.value) localStorage.setItem(TOKEN_KEY, token.value);
  const room: CreatedRoom = { ...(await res.json()), title: String(data.get("title")), createdAt: new Date().toISOString() };
  saveRoom(room);
  show(room);
  $("#result").scrollIntoView({ behavior: "smooth", block: "start" });
});

animation.addEventListener("input", () => {
  preview();
  if (current) show(current);
});

document.addEventListener("click", async (e) => {
  const button = (e.target as HTMLElement).closest<HTMLElement>("[data-copy]");
  if (!button) return;
  await navigator.clipboard.writeText($(button.dataset.copy!).textContent ?? "");
  button.dataset.copied = "";
  setTimeout(() => delete button.dataset.copied, 1200);
});

function show(room: CreatedRoom): void {
  current = room;
  const join = room.audienceUrl;
  const overlayQuery = new URLSearchParams({ origin: position(), animation: animation.value, speed: speed.value });

  $("#result").hidden = false;
  $("#result-title").textContent = room.title;
  $("#result-emoji").textContent = describeRoomEmoji(room.emoji);
  $("#qr").innerHTML = qrSvg(join);
  $("#audience-url").textContent = join;
  $<HTMLAnchorElement>("#open-audience").href = join;
  $<HTMLAnchorElement>("#open-qr").href = `${room.qrUrl}?${overlayQuery}`;
  $<HTMLAnchorElement>("#open-qr3d").href = `${room.audienceUrl}/qr3d?${overlayQuery}`;
  $<HTMLAnchorElement>("#open-display").href = `${room.displayUrl}?${overlayQuery}`;
  $("#display-url").textContent = `${room.displayUrl}?${overlayQuery}`;
  $("#snippet").textContent =
    `<script type="module" src="${room.scriptUrl}"></script>\n` +
    `<live-reactions room="${room.presenterUrl}" origin="${position()}" animation="${animation.value}" speed="${speed.value}"></live-reactions>`;
  $("#qr-snippet").textContent =
    `<script type="module" src="${new URL("/reaccs-qr.js", room.audienceUrl)}"></script>\n` +
    `<reaccs-qr room="${room.audienceUrl}" style="width: 45vh"></reaccs-qr>`;

  const download = $<HTMLAnchorElement>("#download-qr");
  URL.revokeObjectURL(download.href);
  download.href = URL.createObjectURL(new Blob([qrSvg(join)], { type: "image/svg+xml" }));
  download.download = `${room.slug}-qr.svg`;

  void showStatus(room);
  renderRecent();
}

/** "Asleep · 0 of 3 activations used" / "Live until 5:32 PM · 1 of 3 activations used" */
async function showStatus(room: CreatedRoom): Promise<void> {
  const el = $("#result-status");
  el.textContent = "";
  const res = await fetch(room.statusUrl).catch(() => null);
  if (!res?.ok || current !== room) return;
  const s = (await res.json()) as { tier: number; activations: number; activeUntil: number | null; live: boolean };
  const until = s.activeUntil ? new Date(s.activeUntil).toLocaleTimeString(undefined, { timeStyle: "short" }) : "";
  const state = s.live ? `Live until ${until}` : s.activations >= s.tier ? "Exhausted: mint a new room" : "Asleep";
  el.textContent = `${state} · ${s.activations} of ${s.tier} activations used`;
}

function rooms(): CreatedRoom[] {
  // rooms saved before emoji rules existed have unsigned keys and no longer resolve
  // rooms saved before the presenter/audience key split no longer resolve
  return (JSON.parse(localStorage.getItem(ROOMS_KEY) ?? "[]") as CreatedRoom[]).filter((r) => r.presenterUrl);
}

function saveRoom(room: CreatedRoom): void {
  localStorage.setItem(ROOMS_KEY, JSON.stringify([room, ...rooms()].slice(0, 20)));
}

function renderRecent(): void {
  const list = $("#recent-list");
  const all = rooms();
  $("#recent").hidden = all.length === 0;
  list.replaceChildren(
    ...all.map((room) => {
      const li = document.createElement("li");
      const button = Object.assign(document.createElement("button"), { type: "button", textContent: room.title });
      button.addEventListener("click", () => {
        show(room);
        $("#result").scrollIntoView({ behavior: "smooth", block: "start" });
      });
      const time = Object.assign(document.createElement("time"), {
        dateTime: room.createdAt,
        textContent: new Date(room.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" }),
      });
      li.append(button, time);
      return li;
    }),
  );
}

renderRecent();
preview();

// the agent prompt; signed in, it carries the account's default token so an agent can mint as you
$("#agent-prompt").textContent = agentPrompt(location.origin);
void fetch("/api/account").then(async (res) => {
  if (!res.ok) return;
  const account = (await res.json()) as { email: string; tokens: { token?: string }[] };
  $<HTMLAnchorElement>("#account-link").textContent = account.email;
  $("#tier-note").textContent = `Signed in: rooms get 25 activations and are saved to your account.`;
  $("#agent-prompt").textContent = agentPrompt(location.origin, account.tokens.find((t) => t.token)?.token);
});

// ambient demo: previews the position/animation/speed settings (the page never connects to a room:
// a presenter connection would spend one of its activations)
setInterval(() => {
  const emoji = splitEmoji(DEFAULT_PALETTE);
  overlay.emit(emoji[Math.floor(Math.random() * emoji.length)]!, Math.random() < 0.08 ? 40 : 1);
}, 450);
